import React, { useMemo, useState } from 'react'
import { Layout, Menu, Button, Typography, Avatar, Dropdown } from 'antd'
import type { MenuProps } from 'antd'
import * as AntIcons from '@ant-design/icons'
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  RightOutlined,
} from '@ant-design/icons'
import { useGetMenus } from '@/shared/api/client/menu-controller'
import type { ScaleMenu } from '@/shared/api/model/scaleMenu'
import { useNavigate, useLocation, Outlet } from 'react-router-dom'
import { useAuth } from '@/modules/auth'
import { NotificationBell } from '@/modules/in-app-notifications'
import { useTheme } from '@/components/common/ThemeProvider'
import { useUserPreferencesStore } from '@/stores/user-preferences-store'

const { Header, Sider } = Layout
const { Title } = Typography

const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false)
  const menuMode = useUserPreferencesStore((state) => state.menuMode)
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()
  const { dark } = useTheme()

  // Load menus from backend
  const { data: apiMenus } = useGetMenus()

  // Fallback to empty list; component supports both nested (items) and flat (category) lists
  const menusData: any[] = useMemo(() => {
    return (apiMenus as any) || []
  }, [apiMenus])

  const buildMenuKey = (menu: any, parentKey: string, index: number) => {
    const baseKey = menu.page || menu.code || menu.id || menu.label || `menu-${index}`
    return `${parentKey}/${String(baseKey)}::${menu.id || menu.code || index}`
  }

  // Map backend menu to AntD items recursively
  const mapToAntdItems = (items: any[], parentKey = 'root'): MenuProps['items'] => {
    if (!items) return []
    return items.map((m, index) => {
      const key = buildMenuKey(m, parentKey, index)
      const onClick = m.page && !(m.items && m.items.length)
        ? () => navigate(m.page)
        : undefined
      const icon = m.icon && (AntIcons as any)[m.icon]
        ? React.createElement((AntIcons as any)[m.icon])
        : undefined

      if (m.items && Array.isArray(m.items) && m.items.length) {
        return {
          key,
          icon,
          label: m.label,
          children: mapToAntdItems(m.items, key),
        }
      }
      return {
        key,
        icon,
        label: m.label,
        onClick,
      }
    })
  }

  // If backend returns a FLAT list (ScaleMenu[]), try to infer hierarchy using `type==='subMenu'` and permission prefixes
  const buildFromFlat = (flat: ScaleMenu[]): MenuProps['items'] => {
    if (!flat?.length) return []

    const hasSubMenus = flat.some((m) => m.type === 'subMenu')
    if (!hasSubMenus) {
      // Fallback: render as top-level items
      return flat.map((m, index) => ({
        key: buildMenuKey(m, 'flat', index),
        label: m.label,
        onClick: m.page ? () => navigate(m.page!) : undefined,
      }))
    }

    // Build parent-child relations based on permission prefix (closest parent wins)
    type Node = ScaleMenu & { children?: Node[] }
    const nodes: Node[] = flat.map((m) => ({ ...m }))
    const subMenus = nodes.filter((n) => n.type === 'subMenu' && n.permission)

    const parentOf = new Map<Node, Node | null>()
    for (const n of nodes) {
      if (!n.permission) {
        parentOf.set(n, null)
        continue
      }
      // find longest subMenu prefix shorter than this item
      const candidates = subMenus.filter((s) => s !== n && n.permission!.startsWith(s.permission!))
      if (candidates.length) {
        const parent = candidates.sort((a, b) => (b.permission!.length - a.permission!.length))[0]
        parentOf.set(n, parent)
      } else {
        parentOf.set(n, null)
      }
    }

    // attach children
    for (const n of nodes) {
      const p = parentOf.get(n)
      if (p) {
        if (!p.children) p.children = []
        p.children.push(n)
      }
    }

    // top-level: nodes with no parent
    const roots = nodes.filter((n) => parentOf.get(n) === null && (n.type === 'subMenu' || n.page))

    const toAntd = (arr: Node[], parentKey = 'flat-tree'): MenuProps['items'] => arr.map((m, index) => {
      const key = buildMenuKey(m, parentKey, index)
      if (m.children && m.children.length) {
        return {
          key,
          label: m.label,
          children: toAntd(m.children.filter((c) => c !== m), key),
        }
      }
      return {
        key,
        label: m.label,
        onClick: m.page ? () => navigate(m.page!) : undefined,
      }
    })

    return toAntd(roots)
  }

  const menuItems: MenuProps['items'] = useMemo(() => {
    if (Array.isArray(menusData) && menusData.length) {
      // If ANY item has children => nested structure
      if (menusData.some((m: any) => Array.isArray(m.items) && m.items.length)) {
        return mapToAntdItems(menusData)
      }
      // Otherwise treat as flat ScaleMenu[]
      return buildFromFlat(menusData as ScaleMenu[])
    }
    return []
  }, [menusData])

  const findMenuPath = (items: NonNullable<MenuProps['items']>, targetPath: string, path: string[] = []): string[] | null => {
    for (const item of items || []) {
      if (!item || typeof item !== 'object' || !('key' in item)) continue
      const key = String(item.key)
      const nextPath = [...path, key]
      if (key.includes(`${targetPath}::`)) return nextPath
      if ('children' in item && Array.isArray(item.children)) {
        const found = findMenuPath(item.children, targetPath, nextPath)
        if (found) return found
      }
    }
    return null
  }

  const currentRouteKey = `${location.pathname}${location.search}`

  const selectedMenuKey = useMemo(() => {
    const path = findMenuPath(menuItems || [], currentRouteKey) ?? findMenuPath(menuItems || [], location.pathname)
    return path?.[path.length - 1] || currentRouteKey
  }, [currentRouteKey, location.pathname, menuItems])

  const defaultOpenKeys = useMemo(() => {
    const path = findMenuPath(menuItems || [], currentRouteKey) ?? findMenuPath(menuItems || [], location.pathname)
    return path ? path.slice(0, -1) : []
  }, [currentRouteKey, location.pathname, menuItems])

  const handleUserMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'logout') {
      logout()
    } else if (key === 'profile') {
      navigate('/user/profile')
    } else if (key === 'settings') {
      navigate('/user/profile')
    }
  }

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true,
    },
  ]

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {menuMode === 'side' && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          style={{ background: dark ? '#141414' : '#fff' }}
          width={260}
          collapsedWidth={80}
        >
          <div style={{ 
            height: 32, 
            margin: 16, 
            background: dark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(24, 144, 255, 0.12)',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: dark ? 'white' : '#1677ff',
            fontWeight: 'bold'
          }}>
          {collapsed ? 'SF' : 'ScaleFi'}
          </div>
          <Menu
            theme={dark ? 'dark' : 'light'}
            mode="inline"
            selectedKeys={[selectedMenuKey]}
            defaultOpenKeys={defaultOpenKeys}
            expandIcon={(e) => <RightOutlined rotate={e.isOpen ? 90 : 0} />}
            items={menuItems}
          />
        </Sider>
      )}
      <Layout>
        <Header style={{ 
          padding: '0 16px', 
          background: dark ? '#141414' : '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {menuMode === 'side' && (
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                style={{ fontSize: '16px', width: 48, height: 48 }}
              />
            )}
            <Title level={4} style={{ margin: 0, marginLeft: 16, color: dark ? '#fff' : undefined }}>
              ScaleFi Essence
            </Title>
          </div>

          {menuMode === 'top' && (
            <Menu
              mode="horizontal"
              selectedKeys={[selectedMenuKey]}
              triggerSubMenuAction="click"
              expandIcon={(e) => <RightOutlined rotate={e.isOpen ? 90 : 0} />}
              items={menuItems}
              style={{ flex: 1, marginLeft: 24 }}
            />
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <NotificationBell />
            <Dropdown menu={{ items: userMenuItems, onClick: handleUserMenuClick }} placement="bottomRight">
              <div style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                padding: '8px 12px',
                borderRadius: 6,
                transition: 'background-color 0.3s',
                color: dark ? '#fff' : '#000'
              }}>
                <Avatar size="small" icon={<UserOutlined />} style={{ marginRight: 8 }} />
                <span>{user?.username || 'User'}</span>
              </div>
            </Dropdown>
          </div>
        </Header>
        <Outlet />
      </Layout>
    </Layout>
  )
}

export default MainLayout
